/**
 * Slab Foundation Tool - BIM 2025 Grade
 * 
 * Mat foundation / structural slab with:
 * - Sketch-based boundary creation
 * - Slab edge thickeners/thickenings (turned down edges)
 * - Layered structural assembly
 * - Analytical surface model
 * - Slope definition (Slope Arrow)
 */

import { v4 as uuidv4 } from 'uuid';
import { Element, StructuralMaterial, RebarHostData, Constraint, AnalyticalNode } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

// ==================== Foundation Types ====================

export type SlabFoundationType =
    | 'Mat Foundation'      // Uniform thickness
    | 'Slab on Grade'       // With grade beam/thickened edge
    | 'Raft Foundation';    // Ribbed/Cellular (future)

export interface SlabLayer {
    function: 'Structure' | 'Substrate' | 'Insulation' | 'DampProofing'; // Structure, Substrate, etc.
    materialId: string;
    thickness: number;      // mm
}

// ==================== Slab Edges ====================

export type SlabEdgeProfile =
    | 'Thickened'           // Tapered edge
    | 'Grade Beam'          // Rectangular beam
    | 'None';

export interface SlabEdgeData {
    profile: SlabEdgeProfile;
    width: number;          // mm
    depth: number;          // mm below slab
    angle: number;          // degrees (for thickened)
}

// ==================== Enhanced Geometry ====================

export interface EnhancedSlabFoundationGeometryData {
    // Type
    foundationType: SlabFoundationType;

    // Boundary
    boundary: [number, number, number][]; // Closed loop
    holes: [number, number, number][][];   // Inner loops

    // Layers
    layers: SlabLayer[];
    totalThickness: number; // mm

    // Edges
    edgeData?: SlabEdgeData;

    // Positioning
    levelId: string;
    heightOffset: number;   // mm

    // Slope
    hasSlope: boolean;
    slopeArrow?: {
        head: [number, number, number];
        tail: [number, number, number];
        heightAtTail: number;
        heightAtHead: number;
    };

    // Calculated
    area: number;           // m²
    volume: number;         // m³
    weight: number;         // kg
}

// ==================== Enhanced Slab Foundation Element ====================

export interface EnhancedSlabFoundationElement extends Element {
    category: 'Structural Foundations';
    geometryData: EnhancedSlabFoundationGeometryData;

    // Structural
    structuralMaterial: StructuralMaterial;
    rebarHostData: RebarHostData;

    // Analytical
    analyticalModel?: {
        /** Corner nodes of the slab, at the slab mid-plane. */
        nodes: AnalyticalNode[];
        surface: [number, number, number][];
        supportType: 'Spring' | 'Pinned'; // Soil spring
    };
}

// ==================== Tool Options ====================

export interface SlabFoundationToolOptions {
    // Type
    foundationType: SlabFoundationType;
    familyName: string;
    typeName: string;

    // Structure
    thickness: number;          // mm
    materialId: string;

    // Positioning
    levelId: string;
    heightOffset: number;

    // Edges
    edgeProfile: SlabEdgeProfile;
    edgeWidth: number;
    edgeDepth: number;

    // Soil
    subgradeModulus: number;    // kN/m³/m
}

// ==================== Slab Foundation Tool Class ====================

export class SlabFoundationTool extends BaseTool {
    id = 'slab_foundation';
    name = 'Slab Foundation';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    private options: SlabFoundationToolOptions = this.getDefaultOptions();

    private getDefaultOptions(): SlabFoundationToolOptions {
        return {
            foundationType: 'Mat Foundation',
            familyName: 'Foundation Slab',
            typeName: '300mm Concrete',
            thickness: 300,
            materialId: 'mat-concrete-30',
            levelId: 'Level 1',
            heightOffset: 0,
            edgeProfile: 'None',
            edgeWidth: 300,
            edgeDepth: 300,
            subgradeModulus: 20000 // 20000 kN/m³
        };
    }

    /**
     * Set options from UI
     */
    setOptions(options: Partial<SlabFoundationToolOptions>): void {
        this.options = { ...this.options, ...options };
    }

    /**
     * Get current options
     */
    getOptions(): SlabFoundationToolOptions {
        return { ...this.options };
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.points.push(snapped);

        // Check for closure (min 3 points)
        if (this.points.length > 2) {
            const start = this.points[0];
            // Snap to start to close loop
            if (snapped.distanceTo(start) < 0.2) {
                this.points.pop(); // Remove the last point which was close to start
                this.finishSketch();
                return;
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 3) {
                this.finishSketch();
            }
        } else if (event.key === 'Escape') {
            if (this.points.length > 0) {
                this.points = [];
                this.updatePreview();
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.points.length > 0 && this.currentPoint) {
            const previewPoints = [...this.points, this.currentPoint];
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: previewPoints,
                closed: false,
                color: '#808080'
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private finishSketch() {
        if (!this.context) return;

        const boundary: [number, number, number][] = this.points.map(p => [p.x, p.y, p.z]);

        // Calculate polygon area (XY plane)
        const area = this.calculatePolygonArea(boundary);
        const volume = area * (this.options.thickness / 1000);

        const material = this.getMaterial(this.options.materialId);
        const weight = volume * material.properties.density;

        // Build edge data
        let edgeData: SlabEdgeData | undefined;
        if (this.options.edgeProfile !== 'None') {
            edgeData = {
                profile: this.options.edgeProfile,
                width: this.options.edgeWidth,
                depth: this.options.edgeDepth,
                angle: 45
            };

            // Add edge volume approx
            // const perimeter = this.calculatePerimeter(boundary);
            // const edgeArea = (this.options.edgeWidth / 1000) * (this.options.edgeDepth / 1000);
            // Volume adjustment could be added here
        }

        const geometryData: EnhancedSlabFoundationGeometryData = {
            foundationType: this.options.foundationType,
            boundary,
            holes: [],
            layers: [{
                function: 'Structure',
                materialId: this.options.materialId,
                thickness: this.options.thickness
            }],
            totalThickness: this.options.thickness,
            edgeData,
            levelId: this.options.levelId,
            heightOffset: this.options.heightOffset,
            hasSlope: false,
            area,
            volume,
            weight
        };

        const slab: EnhancedSlabFoundationElement = {
            id: uuidv4(),
            category: 'Structural Foundations',
            type: `${this.options.familyName}: ${this.options.typeName}`,
            parameters: this.buildParameters(geometryData),
            constraints: this.getDefaultConstraints(),
            geometryData,
            structuralMaterial: material,
            rebarHostData: {
                validHost: true,
                coverSettings: {
                    top: 50,
                    bottom: 75, // Earth contact
                    sides: 50
                }
            },
            analyticalModel: {
                // Corner nodes at the slab mid-plane — the real analytical
                // discretisation of the foundation surface.
                nodes: this.buildAnalyticalNodes(boundary),
                surface: boundary,
                supportType: 'Spring' // Soil support
            }
        };

        this.context.bimStore.addElement(slab);

        this.points = [];
        this.context.appStore.setToolPreview(null);
    }

    /**
     * Analytical corner nodes: one per boundary vertex at the slab
     * mid-plane (thickness/2 below the top surface), soil-supported.
     */
    private buildAnalyticalNodes(boundary: [number, number, number][]): AnalyticalNode[] {
        const midPlaneZ = boundary.length > 0 ? boundary[0][2] - this.options.thickness / 2000 : 0;
        return boundary.map((p, i) => ({
            id: `${uuidv4()}`,
            position: [p[0], p[1], midPlaneZ] as [number, number, number],
            boundaryConditions: 'Free' as const
        }));
    }

    private calculatePolygonArea(points: [number, number, number][]): number {        let area = 0;
        for (let i = 0; i < points.length; i++) {
            const j = (i + 1) % points.length;
            area += points[i][0] * points[j][1];
            area -= points[j][0] * points[i][1];
        }
        return Math.abs(area / 2);
    }

    private calculatePerimeter(points: [number, number, number][]): number {
        let perimeter = 0;
        for (let i = 0; i < points.length; i++) {
            const p1 = new THREE.Vector3(...points[i]);
            const p2 = new THREE.Vector3(...points[(i + 1) % points.length]);
            perimeter += p1.distanceTo(p2);
        }
        return perimeter;
    }

    private getMaterial(materialId: string): StructuralMaterial {
        return {
            id: materialId,
            name: 'Concrete - Cast-in-Place',
            type: 'Concrete',
            properties: {
                youngsModulus: 30000,
                shearModulus: 12500,
                poissonRatio: 0.2,
                density: 2400,
                thermalExpansionCoefficient: 0.00001,
                concreteCompression: 30
            }
        };
    }

    private buildParameters(geom: EnhancedSlabFoundationGeometryData): Record<string, any> {
        return {
            'Thickness': geom.totalThickness,
            'Area': Math.round(geom.area * 100) / 100,
            'Volume': Math.round(geom.volume * 100) / 100,
            'Weight': Math.round(geom.weight),
            'Elevation': geom.heightOffset,
            'Structural Material': geom.layers[0].materialId,
            'Perimeter': Math.round(this.calculatePerimeter(geom.boundary) * 100) / 100
        };
    }

    private getDefaultConstraints(): Constraint[] {
        return [
            {
                type: 'LEVEL_BASED',
                targetId: this.options.levelId,
                data: { offset: this.options.heightOffset }
            }
        ];
    }
}

// Export types
// Types are already exported at definition
